import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";

import { MarketplaceLaunchableCardWrapper } from "./marketplace-launchable-card";
import { AllTimePopularSection } from "./all-time-popular-section";
import { FeaturedSection } from "./featured-section";
import { SearchBar } from "./search-bar";
import { HoneycombBackground } from "./HoneycombBackground";
import { PhysicalAISection } from "./physical-ai-section";
import type { SearchLaunchablesResponse } from "server/devplane/launchables/api";
import LaunchableInteractive from "components/Launchables/List/LaunchableInteractive";
import { Tabs, TabsList, TabsTrigger } from "components/Graphs/Tabs";
import { useKas } from "contexts/KasContext";
import LaunchableList from "components/Launchables/List";
import { Button } from "components/Graphs/Button";
import { OrgContext } from "contexts/OrgContext";
import { Spinner } from "components/UI-lib";
import {
  POSTHOG_PAYLOAD_FLAGS,
  useFeatureFlagPayload,
} from "utils/posthogFeatureFlags";
import ApplicationPageLayout from "components/ApplicationShell/ApplicationPageLayout";

// Marketplace Configuration - Default Launchable IDs for each section (fallback)
const DEFAULT_MARKETPLACE_CONFIG = {
  featured: [
    "env-36a6a7qnkOMOP2vgiBRaw2e3jpW", // osmo-quick-start
    "env-37wVTMcqDUrCj1irt1ooaGViqlU", // slang-dev
    "env-34fCURommjwcJI3NUiMzlTTyJKS", // Kit App Template
    "env-360InRZzyHqDnJYQKIxaSggF8xI", // nvidia-quantitative-portfolio-optimization
    "env-32kC34ErT9wsqTcJyaKMxBEuhr2", // build an agent
    "env-35JP2ywERLgqtD0b0MIeK1HnF46", // Isaac Launchable
  ],
  allTimePopular: [
    "env-2tYIjRXL4eMCbH9Az8mJC5WPAI4",
    "env-2y9mLGTtOuAHzLbXu3xUY5HLckY",
    "env-2zbpu97JjyY3VxlSITsLgiHOoEK",
    "env-2wggjBvDlVp4pLQD8ytZySh5m8W",
    "env-35JP2ywERLgqtD0b0MIeK1HnF46",
    "env-2u9eZkhqfJrWQdLAQyfskKLUyZ7",
  ],
};

interface MarketplacePlacementsPayload {
  featured?: string[];
  allTimePopular?: string[];
}

type LaunchablesTab = "explore" | "mine";

const getInitialExploreParams = () => {
  const params = new URLSearchParams(window.location.search);
  const gpuType = params.get("gpuType");
  const cloud = params.get("cloud");

  return {
    gpuType,
    cloud,
  };
};

const getLaunchablesTabFromSearch = (
  search: string,
  isUserLoggedIn: boolean,
): LaunchablesTab => {
  const tab = new URLSearchParams(search).get("tab");
  if (tab === "mine" && isUserLoggedIn) return "mine";
  return "explore";
};

const getSearchWithLaunchablesTab = (search: string, tab: LaunchablesTab) => {
  const params = new URLSearchParams(search);
  params.set("tab", tab);
  return `?${params.toString()}`;
};

const LaunchablesPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const initialExploreParams = React.useMemo(
    () => getInitialExploreParams(),
    [],
  );
  const [searchResults, setSearchResults] =
    useState<SearchLaunchablesResponse | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedGPU, setSelectedGPU] = useState<string>(
    initialExploreParams.gpuType ?? "Any",
  );
  const [selectedCloudProvider, setSelectedCloudProvider] = useState<string>(
    initialExploreParams.cloud ?? "Any",
  );
  const [currentCursor, setCurrentCursor] = useState<string>("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = React.useRef<(() => void) | null>(null);
  const { activeOrgId } = useContext(OrgContext);

  const marketplacePlacementsPayload =
    useFeatureFlagPayload<MarketplacePlacementsPayload>(
      POSTHOG_PAYLOAD_FLAGS.LAUNCHABLE_MARKETPLACE_PLACEMENTS,
    );

  const marketplaceConfig = {
    featured:
      marketplacePlacementsPayload?.featured ??
      DEFAULT_MARKETPLACE_CONFIG.featured,
    allTimePopular:
      marketplacePlacementsPayload?.allTimePopular ??
      DEFAULT_MARKETPLACE_CONFIG.allTimePopular,
  };

  const { isUserLoggedIn, isLoading: isAuthLoading } = useKas();
  const activeTab = useMemo(
    () => getLaunchablesTabFromSearch(location.search, Boolean(isUserLoggedIn)),
    [isUserLoggedIn, location.search],
  );

  const handleTabChange = useCallback(
    (value: string) => {
      const nextTab: LaunchablesTab = value === "mine" ? "mine" : "explore";
      history.push({
        pathname: location.pathname,
        search: getSearchWithLaunchablesTab(location.search, nextTab),
        hash: location.hash,
      });
    },
    [history, location.hash, location.pathname, location.search],
  );

  const handleSearch = useCallback(
    (results: SearchLaunchablesResponse | null, append: boolean = false) => {
      if (results === null) {
        setSearchResults(null);
        setCurrentCursor("");
      } else if (append && searchResults) {
        // Append new results to existing ones
        setSearchResults({
          ...results,
          results: [...searchResults.results, ...results.results],
          totalResultsCount: results.totalResultsCount,
          pageResultsCount:
            searchResults.pageResultsCount + results.pageResultsCount,
        });
        setCurrentCursor(results.cursor);
      } else {
        // Replace with new results
        setSearchResults(results);
        setCurrentCursor(results.cursor);
      }
    },
    [searchResults],
  );

  useEffect(() => {
    if (isAuthLoading) return;

    const nextPathname =
      isUserLoggedIn && activeOrgId && location.pathname === "/launchables"
        ? `/org/${activeOrgId}/launchables`
        : location.pathname;
    const nextSearch = getSearchWithLaunchablesTab(location.search, activeTab);

    if (nextPathname === location.pathname && nextSearch === location.search) {
      return;
    }

    history.replace({
      pathname: nextPathname,
      search: nextSearch,
      hash: location.hash,
    });
  }, [
    activeOrgId,
    activeTab,
    history,
    isAuthLoading,
    isUserLoggedIn,
    location.hash,
    location.pathname,
    location.search,
  ]);

  // Infinite scroll: Load more when reaching bottom
  useEffect(() => {
    const handleScroll = () => {
      // Check if we're near the bottom of the page (within 500px)
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop;
      const clientHeight = document.documentElement.clientHeight;

      if (
        scrollHeight - scrollTop - clientHeight < 500 &&
        currentCursor &&
        !isLoadingMore &&
        searchResults &&
        activeTab === "explore"
      ) {
        // Trigger load more
        if (loadMoreRef.current) {
          loadMoreRef.current();
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [currentCursor, isLoadingMore, searchResults, activeTab]);

  // Don't render the page until the user info is loaded to avoid the page jumping.
  if (isAuthLoading) {
    return null;
  }

  return (
    <>
      {/* Animated honeycomb background */}
      <div className="relative w-full overflow-hidden">
        <HoneycombBackground />

        {/* Content container */}
        <ApplicationPageLayout className="relative z-10 py-10 sm:px-6 lg:px-8">
          {/* <InitialTemplateModal /> */}
          <div className="mx-auto px-4">
            {/* Header */}
            <div>
              <div>
                <div className="flex items-start justify-between w-full gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-primary mb-4 bg-gradient-to-r from-white via-white to-gray-400 bg-clip-text text-transparent">
                      Launchables
                    </h1>
                    <p className="text-secondary max-w-2xl text-sm">
                      Deploy pre-configured GPU environments with one click.
                      Share and discover turnkey templates for AI workflows and
                      GPU-accelerated software.
                    </p>
                  </div>

                  <Button onClick={() => history.push("/launchables/create")}>
                    Create Launchable
                  </Button>
                </div>

                {/* Tabs */}
                {isUserLoggedIn && (
                  <div className="flex justify-start mt-6">
                    <Tabs value={activeTab} onValueChange={handleTabChange}>
                      <TabsList variant="solid">
                        <TabsTrigger value="explore">Explore</TabsTrigger>
                        <TabsTrigger value="mine" disabled={!isUserLoggedIn}>
                          My Launchables
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                )}
                {activeTab === "explore" && (
                  <div className="mt-8">
                    <SearchBar
                      onSearch={handleSearch}
                      searchValue={searchValue}
                      setSearchValue={setSearchValue}
                      selectedGPU={selectedGPU}
                      setSelectedGPU={setSelectedGPU}
                      selectedCloudProvider={selectedCloudProvider}
                      setSelectedCloudProvider={setSelectedCloudProvider}
                      currentCursor={currentCursor}
                      onLoadMoreRef={loadMoreRef}
                      isLoadingMore={isLoadingMore}
                      setIsLoadingMore={setIsLoadingMore}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="mx-auto px-4">
            <div className="w-full">
              {activeTab === "explore" && (
                <>
                  {!searchResults && (
                    <div className="space-y-12 mt-6 mb-12">
                      <FeaturedSection
                        launchableIds={marketplaceConfig.featured}
                      />
                      <PhysicalAISection />
                      <AllTimePopularSection
                        launchableIds={marketplaceConfig.allTimePopular}
                      />
                      {/* <BrowseCategoriesSection /> */}
                    </div>
                  )}

                  {searchResults && (
                    <>
                      {/* Toolbar */}
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 my-6">
                        <div className="flex items-center gap-4 justify-between w-full">
                          <p className="text-secondary">
                            {searchResults?.results?.length || 0} results found
                          </p>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setSearchResults(null);
                              setSearchValue("");
                              setSelectedGPU("Any");
                              setSelectedCloudProvider("Any");
                              setCurrentCursor("");
                            }}
                          >
                            Clear search
                          </Button>
                        </div>
                      </div>

                      {/* Software Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {searchResults?.results?.map((result) => (
                          <MarketplaceLaunchableCardWrapper
                            key={result.environment_id}
                            environment={result}
                          />
                        ))}
                      </div>

                      {/* Loading more indicator */}
                      {isLoadingMore && (
                        <div className="flex justify-center py-8">
                          <Spinner type="primary" />
                        </div>
                      )}

                      {/* Empty State */}
                      {(searchResults?.results?.length === 0 ||
                        !searchResults?.results) && (
                        <div className="text-center py-18">
                          <div className="mb-4">
                            <LaunchableInteractive
                              isSectionInfoEnabled={false}
                              isAnimationEnabled={true}
                            />
                          </div>
                          <h3 className="text-xl font-semibold mb-2 text-primary">
                            No Launchables found
                          </h3>
                          <p className="text-secondary mb-4">
                            Try adjusting your search or filters to find what
                            you&apos;re looking for.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {activeTab === "mine" && isUserLoggedIn && <LaunchableList />}
            </div>
          </div>
        </ApplicationPageLayout>
      </div>
    </>
  );
};

export default LaunchablesPage;
